<template>
  <UIButton
    v-if="canPrintReceipt"
    :label="t('actions.print')"
    :disabled="isPrinting"
    :loading="isPrinting"
    @click="printReceipt(response)"
  />
</template>

<script setup lang="ts">
  import {
    ApplyResponseCode,
    type ProviderApplyResponseView,
  } from "@versatile-credit-apply/omni-apply-client"

  import {
    usePrintReceipt,
    useProviderConfig,
    useProviderI18n,
  } from "@versatile-credit-apply/sdk"
  import { computed, onMounted, ref } from "vue"

  interface Props {
    response: ProviderApplyResponseView
  }

  const props = defineProps<Props>()

  const config = useProviderConfig(() => props.response.providerKey)

  const printMode = computed<"automatic" | "manual" | undefined>(
    () => config.value?.properties?.printMode
  )

  const isPrinting = ref(false)
  const printReceipt = usePrintReceipt(isPrinting)

  const canPrintReceipt = computed<boolean>(() => {
    if (printMode.value == undefined) {
      return false
    }

    const printableResponseCodes = [
      ApplyResponseCode.APPROVED,
      ApplyResponseCode.DECLINED,
      ApplyResponseCode.PENDING,
    ]

    return printableResponseCodes.includes(props.response.responseCode)
  })

  const { t } = useProviderI18n(() => props.response.providerKey)

  onMounted(async () => {
    if (printMode.value === "automatic" && canPrintReceipt.value) {
      await printReceipt(props.response)
    }
  })
</script>

<i18n lang="yaml" locale="en">
actions:
  print: Print Receipt
</i18n>
